import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  getGetApiV1MissionsMissionIdQueryKey,
  usePostApiV1MissionsMissionIdMembersMembershipIdLeader,
  usePostApiV1MissionsMissionIdMembersMembershipIdRemove,
  usePostApiV1MissionsMissionIdMembersMembershipIdExclude,
} from '@/shared/api/generated/missions/missions';
import type { MissionMemberDto } from '@/shared/api/generated/model';
import { problemDetail } from '@/shared/api/problem';
import { useIsMissionControl } from '@/features/missions/model/use-mission-control';
import { useLocale } from '@/shared/lib/i18n/locale-context';
import { Button } from '@/shared/ui/Button';

/**
 * What **this mission's** control may do to one member (decision of 22 August 2026).
 *
 * ⚠️ **Gated on the open shift, not on a role, and the difference is the whole point.** Every Desk
 * Control operator passes the control policy — that is what lets them answer a mission's offer at
 * all — so a role check here would offer these buttons to every operator looking at the mission,
 * and every one of those clicks would come back 403. The server applies exactly this test.
 *
 * ⚠️ The Head of Control is not special-cased here either, matching the server: §23.1 has them
 * following a mission until an owner is assigned, and the way to follow one is to take the watch.
 */
export function MissionControlActions({
  member,
  missionId,
  missionType,
  missionStatus,
}: {
  member: MissionMemberDto;
  missionId: string;
  missionType: string;
  missionStatus: string;
}) {
  const { t } = useLocale();
  const queryClient = useQueryClient();

  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Shared with the other panels on this page, so it costs no extra request.
  const iAmTheControl = useIsMissionControl(missionId);

  const refresh = async () => {
    setError(null);
    await queryClient.invalidateQueries({
      queryKey: getGetApiV1MissionsMissionIdQueryKey(missionId),
    });
  };

  const onError = (cause: unknown) => setError(problemDetail(cause) ?? t.missions.actionFailed);

  const leader = usePostApiV1MissionsMissionIdMembersMembershipIdLeader({
    mutation: { onSuccess: refresh, onError },
  });
  const remove = usePostApiV1MissionsMissionIdMembersMembershipIdRemove({
    mutation: {
      onSuccess: async () => {
        setRemoving(false);
        await refresh();
      },
      onError,
    },
  });

  // ⚠️ **A different act from removing, with a different button and a different window.** Removing
  // ends a rescuer's participation in work already under way; excluding frees a RESERVED place
  // before the day, and the place goes to whoever is next in the queue the moment it returns.
  const exclude = usePostApiV1MissionsMissionIdMembersMembershipIdExclude({
    mutation: {
      onSuccess: async () => {
        setRemoving(false);
        await refresh();
      },
      onError,
    },
  });

  const isRegistration = missionType === 'SubMission';

  if (!iAmTheControl) {
    return <span className="text-xs text-muted-foreground">{t.missions.detailControlOnly}</span>;
  }

  // ⚠️ **Two windows, because the two families are managed at opposite ends of their life.** A
  // search's team is led and pruned while it RUNS; a sub-mission's roster is settled in the run-up
  // to the day, and once the occasion has started the honest record is who attended. The server
  // answers 409 on either side of these.
  if (isRegistration ? missionStatus !== 'Approved' : missionStatus !== 'Active') {
    return <span className="text-muted-foreground">{t.missions.detailNone}</span>;
  }

  // Somebody who has left, been removed or finished is not on the team to lead it or be taken off
  // it. Drawn as nothing rather than as disabled buttons: a removed row already says why.
  //
  // ⚠️ `Waiting` counts for a registration: somebody in the queue can be taken off it, and the
  // server allows exactly that — they simply free no place, because they held none.
  const onTheMission =
    member.state === 'Joined' ||
    member.state === 'Active' ||
    member.state === 'IdleWarned' ||
    (isRegistration && member.state === 'Waiting');

  if (!onTheMission) {
    return <span className="text-muted-foreground">{t.missions.detailNone}</span>;
  }

  if (removing) {
    return (
      <RemoveForm
        pending={remove.isPending || exclude.isPending}
        lede={isRegistration ? t.missions.subExcludeLede : t.missions.detailRemoveDialogLede}
        confirmLabel={isRegistration ? t.missions.subExclude : t.missions.detailConfirmRemove}
        onCancel={() => setRemoving(false)}
        onSubmit={(reason) =>
          isRegistration
            ? exclude.mutate({ missionId, membershipId: member.membershipId, data: { reason } })
            : remove.mutate({ missionId, membershipId: member.membershipId, data: { reason } })
        }
        error={error}
      />
    );
  }

  // ⚠️ **No field leader on a registration.** §23.6 names the leader of a SEARCH — the person who
  // directs it on the ground — and a lecture has no ground to direct. Offering the button here
  // would name a leader of an exhibition.
  if (isRegistration) {
    return (
      <div className="flex flex-col items-start gap-1">
        <Button variant="danger" onClick={() => setRemoving(true)}>
          {t.missions.subExclude}
        </Button>

        {error ? <span className="text-xs text-danger">{error}</span> : null}
      </div>
    );
  }

  return (
    <div className="flex flex-col items-start gap-1">
      <Button
        variant="ghost"
        disabled={leader.isPending}
        onClick={() =>
          leader.mutate({
            missionId,
            membershipId: member.membershipId,
            data: { named: !member.isLeader },
          })
        }
      >
        {member.isLeader ? t.missions.detailStandDownLeader : t.missions.detailNameLeader}
      </Button>

      {!member.isLeader ? (
        <span className="text-xs text-muted-foreground">{t.missions.detailLeaderHint}</span>
      ) : null}

      <Button variant="danger" onClick={() => setRemoving(true)}>
        {t.missions.detailRemoveMember}
      </Button>

      {error ? <span className="text-xs text-danger">{error}</span> : null}
    </div>
  );
}

/**
 * The reason, which the server requires.
 *
 * ⚠️ A form rather than a confirmation, because §23.5's idle sweep writes its own reason
 * automatically and a removal by a person with none beside it is the one entry in a mission's
 * record nobody can review. The consequence is stated above the field: they lose the map and the
 * chat immediately, and are told why.
 */
function RemoveForm({
  pending,
  lede,
  confirmLabel,
  onCancel,
  onSubmit,
  error,
}: {
  pending: boolean;

  /** ⚠️ What this particular removal costs the person — it is not the same sentence for both. */
  lede: string;
  confirmLabel: string;
  onCancel: () => void;
  onSubmit: (reason: string) => void;
  error: string | null;
}) {
  const { t } = useLocale();
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);

  const empty = reason.trim() === '';

  return (
    <form
      className="flex w-56 flex-col gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        setTouched(true);

        if (!empty) {
          onSubmit(reason.trim());
        }
      }}
    >
      <p className="text-xs text-muted-foreground">{lede}</p>

      <label className="flex flex-col gap-1 text-xs">
        <span className="text-muted-foreground">{t.missions.detailRemoveReason}</span>
        <textarea
          className="min-h-16 w-full rounded-lg border border-border bg-surface p-2 text-sm"
          value={reason}
          placeholder={t.missions.detailRemoveReasonPlaceholder}
          onChange={(event) => setReason(event.target.value)}
        />
      </label>

      {touched && empty ? (
        <span role="alert" className="text-xs text-danger">
          {t.missions.detailRemoveReasonRequired}
        </span>
      ) : null}

      {error ? <span className="text-xs text-danger">{error}</span> : null}

      <div className="flex gap-2">
        <Button type="submit" variant="danger" disabled={pending}>
          {confirmLabel}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>
          {t.missions.cancel}
        </Button>
      </div>
    </form>
  );
}
